import { z } from 'zod';

import { stableStringify } from './stableStringify';
import type { JSONSchema } from './types';

/**
 * Tracks the JSON path to arrays that have uniqueItems: true.
 * Used to apply uniqueness validation after the converted schema is built.
 */
type UniqueItemsPath = string[];

/**
 * Recursively walks a JSON Schema to find all arrays with uniqueItems: true
 * and record their paths. Used to apply uniqueness validation via superRefine.
 */
function findUniqueItemsPaths(schema: JSONSchema, currentPath: string[] = []): UniqueItemsPath[] {
  const paths: UniqueItemsPath[] = [];

  function walk(s: JSONSchema, path: string[]): void {
    if (s.type === 'array') {
      if (s.uniqueItems === true) {
        paths.push([...path]);
      }
      if (s.items) {
        walk(s.items as JSONSchema, [...path, 'items']);
      }
    } else if (s.type === 'object' && s.properties) {
      for (const [key, propSchema] of Object.entries(s.properties)) {
        walk(propSchema as JSONSchema, [...path, 'properties', key]);
      }
    }
  }

  walk(schema, currentPath);
  return paths;
}

/**
 * Validates that all items in an array are unique using deep value comparison.
 * Uses stableStringify to handle objects with different key orders.
 */
function validateUniqueItems(items: unknown[]): boolean {
  const seen = new Set<string>();
  for (const item of items) {
    const key = stableStringify(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
  }
  return true;
}

/**
 * Recursively navigates a schema path and validates uniqueItems at every matching location.
 *
 * Schema paths contain 'items' and 'properties' segments that don't map 1:1 to data:
 * - 'properties' is skipped (it's just a schema grouping keyword)
 * - 'items' means "fan out across every element of the current array"
 *
 * For example, path ['items', 'properties', 'tags'] on [{tags:['a','a']}]:
 *   - 'items'      → iterate each element of the outer array
 *   - 'properties' → skip
 *   - 'tags'       → navigate into element.tags
 *   - empty path   → validate uniqueness of element.tags
 */
function validateUniquenessAtPath(
  data: unknown,
  remainingPath: string[],
  accessPath: Array<string | number>,
  ctx: z.RefinementCtx,
): void {
  if (remainingPath.length === 0) {
    if (Array.isArray(data) && !validateUniqueItems(data)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Array items must be unique',
        path: accessPath,
      });
    }
    return;
  }

  const [segment, ...rest] = remainingPath;

  if (segment === 'properties') {
    validateUniquenessAtPath(data, rest, accessPath, ctx);
  } else if (segment === 'items') {
    if (Array.isArray(data)) {
      data.forEach((item, index) => {
        validateUniquenessAtPath(item, rest, [...accessPath, index], ctx);
      });
    }
  } else if (data !== null && data !== undefined && typeof data === 'object' && !Array.isArray(data)) {
    const next = (data as Record<string, unknown>)[segment];
    validateUniquenessAtPath(next, rest, [...accessPath, segment], ctx);
  }
}

/**
 * Applies uniqueItems validation to the appropriate paths in a Zod schema.
 *
 * Since we can't easily traverse and modify nested Zod schemas, we apply
 * a top-level superRefine that validates uniqueness at each path.
 */
function applyUniqueItemsValidation(zodSchema: z.ZodType, uniqueItemsPaths: UniqueItemsPath[]): z.ZodType {
  if (uniqueItemsPaths.length === 0) {
    return zodSchema;
  }

  return zodSchema.superRefine((data, ctx) => {
    for (const path of uniqueItemsPaths) {
      validateUniquenessAtPath(data, path, [], ctx);
    }
  });
}

/**
 * Names a value the way the validation messages below describe it.
 */
function describeValue(input: unknown): string {
  if (input === null) {
    return 'null';
  }
  if (typeof input === 'number') {
    return Number.isNaN(input) ? 'nan' : 'number';
  }
  if (Array.isArray(input)) {
    return 'array';
  }
  if (typeof input === 'object') {
    return 'object';
  }
  return typeof input;
}

function quoteOptions(values: readonly unknown[]): string {
  return values.map((value) => `'${String(value)}'`).join(' | ');
}

/**
 * Produces the validation message for a failed constraint.
 *
 * These strings are shown directly under the form field, so they are written
 * out here instead of relying on Zod's built-in wording, which differs between
 * Zod releases. Every schema and constraint built below passes this in so the
 * text a person sees stays the same.
 */
function fieldMessage(issue: z.core.$ZodRawIssue): string | undefined {
  switch (issue.code) {
    case 'invalid_type': {
      if (issue.input === undefined) {
        return 'Required';
      }
      if (issue.expected === 'int') {
        return 'Expected integer, received float';
      }
      return `Expected ${issue.expected}, received ${describeValue(issue.input)}`;
    }
    case 'too_small': {
      const minimum = String(issue.minimum);
      if (issue.origin === 'string') {
        return `String must contain at least ${minimum} character(s)`;
      }
      if (issue.origin === 'array') {
        return `Array must contain at least ${minimum} element(s)`;
      }
      return `Number must be greater than ${issue.inclusive ? 'or equal to ' : ''}${minimum}`;
    }
    case 'too_big': {
      const maximum = String(issue.maximum);
      if (issue.origin === 'string') {
        return `String must contain at most ${maximum} character(s)`;
      }
      if (issue.origin === 'array') {
        return `Array must contain at most ${maximum} element(s)`;
      }
      return `Number must be less than ${issue.inclusive ? 'or equal to ' : ''}${maximum}`;
    }
    case 'not_multiple_of':
      return `Number must be a multiple of ${String(issue.divisor)}`;
    case 'invalid_value': {
      const options = quoteOptions(issue.values);
      if (issue.input === undefined) {
        return 'Required';
      }
      if (typeof issue.input !== 'string') {
        return `Expected ${options}, received ${describeValue(issue.input)}`;
      }
      return `Invalid enum value. Expected ${options}, received '${issue.input}'`;
    }
    default:
      return undefined;
  }
}

/**
 * The number keywords the earlier hand-written builder read. Anything else in a
 * schema it ignored, so `normalizeSchema` keeps this list and drops the rest.
 */
const NUMBER_KEYWORDS = ['minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum', 'multipleOf'] as const;

/**
 * Reduces a JSON Schema to the keywords this package validates.
 *
 * The converter reads every keyword in a schema; the builder it replaces read
 * `type`, `enum`, `required`, `properties`, `items`, `minLength`, `maxLength`,
 * `minItems`, `maxItems`, `minimum`, `maximum`, `exclusiveMinimum`,
 * `exclusiveMaximum` and `multipleOf`, and ignored everything else. Converting
 * the reduced copy keeps the keywords that were ignored — `pattern`, `format`,
 * `const`, `default`, `additionalProperties`, `minProperties`, `allOf` and the
 * rest — ignored, so no form starts rejecting a value it accepts today.
 *
 * `uniqueItems` is dropped too, because `applyUniqueItemsValidation` checks it.
 *
 * A node whose `type` the builder did not recognize accepted any value. The copy
 * of such a node keeps no keywords at all, which converts to a schema that
 * accepts anything — for that node only, leaving the rest of the schema
 * validated.
 */
function normalizeSchema(schema: JSONSchema): JSONSchema {
  switch (schema.type) {
    case 'number':
    case 'integer':
      return normalizeNumber(schema);
    case 'string':
      return normalizeString(schema);
    case 'boolean':
      return { type: 'boolean' };
    case 'object':
      return normalizeObject(schema);
    case 'array':
      return normalizeArray(schema);
    default:
      return {};
  }
}

/** A subschema, where JSON Schema also allows a boolean the builder did not read. */
function normalizeSubschema(schema: unknown): JSONSchema {
  return isSchemaObject(schema) ? normalizeSchema(schema) : {};
}

function normalizeNumber(schema: JSONSchema): JSONSchema {
  const normalized: JSONSchema = { type: schema.type };
  for (const keyword of NUMBER_KEYWORDS) {
    const bound = schema[keyword];
    if (typeof bound === 'number') {
      normalized[keyword] = bound;
    }
  }
  return normalized;
}

function normalizeString(schema: JSONSchema): JSONSchema {
  const options = Array.isArray(schema.enum) ? schema.enum.filter((value) => typeof value === 'string') : [];
  if (options.length > 0) {
    // An enum of strings describes the whole field: the builder read no length from it.
    return { type: 'string', enum: options };
  }

  const normalized: JSONSchema = { type: 'string' };
  if (typeof schema.minLength === 'number') {
    normalized.minLength = schema.minLength;
  }
  if (typeof schema.maxLength === 'number') {
    normalized.maxLength = schema.maxLength;
  }
  return normalized;
}

function normalizeObject(schema: JSONSchema): JSONSchema {
  const source = isSchemaObject(schema.properties) ? schema.properties : {};
  const properties: Record<string, JSONSchema> = {};
  for (const [name, propSchema] of Object.entries(source)) {
    properties[name] = normalizeSubschema(propSchema);
  }

  const normalized: JSONSchema = { type: 'object', properties };
  const required = Array.isArray(schema.required) ? schema.required.filter((name) => name in properties) : [];
  if (required.length > 0) {
    normalized.required = required;
  }
  return normalized;
}

function normalizeArray(schema: JSONSchema): JSONSchema {
  // A list of `items` describes a tuple, which the builder did not read, so every
  // element accepted anything.
  const items = schema.items === undefined || Array.isArray(schema.items) ? {} : normalizeSubschema(schema.items);

  const normalized: JSONSchema = { type: 'array', items };
  if (typeof schema.minItems === 'number') {
    normalized.minItems = schema.minItems;
  }
  if (typeof schema.maxItems === 'number') {
    normalized.maxItems = schema.maxItems;
  }
  return normalized;
}

/**
 * A JSON Schema node, as opposed to the boolean JSON Schema also allows, or a
 * keyword value that is not a schema at all.
 */
function isSchemaObject(value: unknown): value is JSONSchema {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Drops values the schema does not declare, which is what the object schemas the
 * builder produced did.
 *
 * Reads the normalized schema, so `properties` holds every declared name and
 * `items` is a single schema.
 */
function pruneUndeclared(schema: JSONSchema, value: unknown): unknown {
  if (schema.type === 'object' && isSchemaObject(value)) {
    const properties = isSchemaObject(schema.properties) ? schema.properties : {};
    const pruned: Record<string, unknown> = {};
    for (const [name, propSchema] of Object.entries(properties)) {
      if (Object.hasOwn(value, name)) {
        pruned[name] = isSchemaObject(propSchema) ? pruneUndeclared(propSchema, value[name]) : value[name];
      }
    }
    return pruned;
  }

  const items = schema.items;
  if (schema.type === 'array' && Array.isArray(value) && isSchemaObject(items)) {
    return value.map((entry) => pruneUndeclared(items, entry));
  }

  return value;
}

/**
 * Wraps the converted schema so that every message comes from `fieldMessage`.
 *
 * Zod takes an error map per parse and consults it for any issue the schema does
 * not name itself. The converted schema names none, so the single map here also
 * covers nested fields and the constraints attached to them. The wrapper exists
 * because callers hold the result and parse through it without passing a map.
 */
function withFieldMessages(converted: z.ZodType, normalized: JSONSchema): z.ZodType {
  return z.unknown().transform((data, ctx) => {
    const result = converted.safeParse(data, { error: fieldMessage });

    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({ ...issue });
      }
      return z.NEVER;
    }

    return pruneUndeclared(normalized, result.data);
  });
}

/**
 * Converts a JSON Schema to a Zod schema.
 *
 * Validates the keywords listed on `normalizeSchema`, with the messages in
 * `fieldMessage` rather than Zod's own wording, so the copy under a form field
 * does not move when Zod's does.
 *
 * Handles `uniqueItems: true` by adding custom validation via .superRefine(),
 * since Zod reports one issue per duplicate item rather than one on the array.
 *
 * @param schema - A valid JSON Schema object
 * @returns A Zod schema that validates according to the JSON Schema
 */
export function schemaToZod(schema: JSONSchema): z.ZodType {
  const uniqueItemsPaths = findUniqueItemsPaths(schema);
  const normalized = normalizeSchema(schema);
  const converted = z.fromJSONSchema(normalized);
  return applyUniqueItemsValidation(withFieldMessages(converted, normalized), uniqueItemsPaths);
}
